/**
 * FormValidators class converted from Dart/Flutter.
 * Provides synchronous client-side validation and asynchronous API validation checks.
 *
 * NOTE ON BASE URL:
 * The original Dart code used Flutter's platform detection (kIsWeb, Platform.isAndroid).
 * This JS version simplifies the logic, defaulting to localhost for web/desktop debugging
 * and noting the common Android emulator IP (10.0.2.2) in comments, as this is typically
 * handled via environment configuration in real-world JS/React Native apps.
 */
export default class FormValidators {
    constructor() {
        this._baseUrl = FormValidators._resolveBaseUrl();
    }

    /**
     * Helper to resolve the base URL, simplified for a generic JS environment.
     * @returns {string} The base URL for the API.
     */
    static _resolveBaseUrl() {
        // In a real application, this should be configured via environment variables.
        // We'll default to the standard localhost used in the original Dart file.
        // If targeting Android emulator from Node/server side, you might use 'http://10.0.2.2:5000/api'
        // If targeting iOS simulator, you might use 'http://127.0.0.1:5000/api'
        return `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/api`;
    }

    // --- Synchronous Static Validators ---

    /**
     * A validator for full names. Checks if the name is not empty.
     * @param {string | null | undefined} value
     * @returns {string | null} Error message or null if valid.
     */
    static validateName(value) {
        if (!value || value.trim().length === 0) {
            return 'Please enter your full name';
        }
        return null;
    }

    /**
     * A validator for phone numbers. Checks for E.164 format: + followed by 8 to 15 digits.
     * @param {string | null | undefined} value
     * @returns {string | null} Error message or null if valid.
     */
    static validatePhoneNumber(value) {
        if (!value || value.trim().length === 0) {
            return 'Please enter your phone number';
        }

        // E.164 format: + followed by 8 to 15 digits
        const regex = /^\+[1-9]\d{7,14}$/;
        const trimmedValue = value.trim();

        if (!regex.test(trimmedValue)) {
            return 'Please enter a valid international phone number (e.g. +94771234567)';
        }

        return null;
    }

    /**
     * A validator for email addresses. Checks for a non-empty string and a valid format.
     * @param {string | null | undefined} value
     * @returns {string | null} Error message or null if valid.
     */
    static validateEmail(value) {
        if (!value || value.length === 0) {
            return 'Please enter your email address';
        }
        // A simple regex for basic email format validation.
        const emailRegex = /^[^@]+@[^@]+\.[^@]+$/;
        if (!emailRegex.test(value)) {
            return 'Please enter a valid email address';
        }
        return null;
    }

    /**
     * A validator for passwords. Checks for non-empty and a minimum length of 6 characters.
     * @param {string | null | undefined} value
     * @returns {string | null} Error message or null if valid.
     */
    static validatePassword(value) {
        if (!value || value.length === 0) {
            return 'Please enter a password';
        }
        if (value.length < 6) {
            return 'Password must be at least 6 characters long';
        }
        return null;
    }

    /**
     * A validator for password confirmation.
     * @param {string | null | undefined} value - The confirmation password.
     * @param {string} password - The original password to match against.
     * @returns {string | null} Error message or null if valid.
     */
    static validateConfirmPassword(value, password) {
        if (!value || value.length === 0) {
            return 'Please confirm your password';
        }
        if (value !== password) {
            return 'Passwords do not match';
        }
        return null;
    }

    /**
     * A validator for dropdown menus. Checks if an item has been selected (value is not null/undefined).
     * @param {Object | null | undefined} value
     * @returns {string | null} Error message or null if valid.
     */
    static validateDropdown(value) {
        if (value === null || value === undefined) {
            return 'Please make a selection';
        }
        return null;
    }

    /**
     * A validator for Sri Lankan addresses. Checks for non-empty, minimum length (10), and valid characters.
     * @param {string | null | undefined} value
     * @returns {string | null} Error message or null if valid.
     */
    static validateAddress(value) {
        const trimmedValue = value?.trim();

        // 1. Check if null or empty
        if (!trimmedValue) {
            return 'Please enter your address';
        }

        // 2. Check minimum length
        if (trimmedValue.length < 10) {
            return 'Address is too short. Please provide more details.';
        }

        // 3. Regex: Allow letters, numbers, spaces, and specific punctuation
        // Allowed: a-z, A-Z, 0-9, space, comma (,), dot (.), slash (/), hash (#), quote ('), hyphen (-), backslash (\)
        const nameExp = /^[a-zA-Z0-9\s,./#'\\-]+$/;

        if (!nameExp.test(trimmedValue)) {
            return 'Address contains invalid characters';
        }

        return null;
    }

    /**
     * A validator for Sri Lankan NIC numbers. Supports both old (9 digits + V/X) and new (12 digits) formats.
     * @param {string | null | undefined} value
     * @returns {string | null} Error message or null if valid.
     */
    static validateNIC(value) {
        if (!value || value.length === 0) {
            return 'Please enter your NIC number';
        }
        const nic = value.trim().toUpperCase();

        // Old NIC format: 9 digits followed by 'V' or 'X'
        const oldNicRegex = /^\d{9}[VX]$/;
        // New NIC format: 12 digits
        const newNicRegex = /^\d{12}$/;

        if (oldNicRegex.test(nic) || newNicRegex.test(nic)) {
            return null;
        } else {
            return 'Please enter a valid Sri Lankan NIC number (e.g., 199012345678 or 901234567V)';
        }
    }

    /**
     * A validator for birthday, ensuring the age is 17 or older (for travelers).
     * @param {string | null | undefined} value - Date string (e.g., YYYY-MM-DD).
     * @returns {string | null} Error message or null if valid.
     */
    static validateBirthdayForTraveler(value) {
        if (!value || value.length === 0) {
            return 'Please enter your birthday';
        }

        const birthday = new Date(value);
        // Check for valid date parsing. new Date("invalid") results in Invalid Date.
        if (isNaN(birthday.getTime())) {
            return 'Please enter a valid date format (e.g., YYYY-MM-DD)';
        }

        const today = new Date();

        // Calculate age
        let calculatedAge = today.getFullYear() - birthday.getFullYear();

        // Adjust age if the birthday hasn't occurred yet this year
        if (today.getMonth() < birthday.getMonth() ||
            (today.getMonth() === birthday.getMonth() && today.getDate() < birthday.getDate())) {
            calculatedAge--;
        }

        if (calculatedAge < 17) {
            return 'You must be at least 17 years old';
        }

        return null;
    }

    /**
     * A validator for birthday, ensuring the age is 20 or older (for guides/hotel owners).
     * @param {string | null | undefined} value - Date string (e.g., YYYY-MM-DD).
     * @returns {string | null} Error message or null if valid.
     */
    static validateBirthdayForGuidHotel(value) {
        if (!value || value.length === 0) {
            return 'Please enter your birthday';
        }

        const birthday = new Date(value);
        if (isNaN(birthday.getTime())) {
            return 'Please enter a valid date format (e.g., YYYY-MM-DD)';
        }

        const today = new Date();

        // Calculate age
        let calculatedAge = today.getFullYear() - birthday.getFullYear();

        // Adjust age if the birthday hasn't occurred yet this year
        if (today.getMonth() < birthday.getMonth() ||
            (today.getMonth() === birthday.getMonth() && today.getDate() < birthday.getDate())) {
            calculatedAge--;
        }

        if (calculatedAge < 20) {
            return 'You must be at least 20 years old';
        }

        return null;
    }

    // --- Asynchronous HTTP Validators ---

    /**
     * Validates NIC and Date of Birth against the API.
     * @param {string} nic
     * @param {string} dob - Date string (e.g., YYYY-MM-DD)
     * @returns {Promise<boolean>} True if valid.
     * @throws {Error} If validation fails or network error occurs.
     */
    async validateNicDob(nic, dob) {
        try {
            const response = await fetch(`${this._baseUrl}/validate-nic-dob`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ nic, dob }),
            });

            const jsonResponse = await response.json();

            if (response.ok) { // `response.ok` is true for 200-299 status codes
                return jsonResponse.isValid === true;
            } else {
                const message = jsonResponse.message || 'NIC/DOB validation failed';
                throw new Error(message);
            }
        } catch (e) {
            throw new Error(`Failed to validate NIC and DOB: ${e.message || e}`);
        }
    }

    /**
     * Validates NIC and Gender against the API.
     * @param {string} nic
     * @param {number} genderId
     * @returns {Promise<boolean>} True if valid.
     * @throws {Error} If validation fails or network error occurs.
     */
    async validateNicGender(nic, genderId) {
        try {
            const response = await fetch(`${this._baseUrl}/validate-nic-gender`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ nic, genderId }),
            });

            const jsonResponse = await response.json();

            if (response.ok) {
                return jsonResponse.isValid === true;
            } else {
                const message = jsonResponse.message || 'NIC/Gender validation failed';
                throw new Error(message);
            }
        } catch (e) {
            throw new Error(`Failed to validate NIC and Gender: ${e.message || e}`);
        }
    }

    /**
     * Validates Guide NIC and Date of Birth against the API.
     * @param {string} nic
     * @param {string} dob - Date string (e.g., YYYY-MM-DD)
     * @returns {Promise<boolean>} True if valid.
     * @throws {Error} If validation fails or network error occurs.
     */
    async validateGuideNicDob(nic, dob) {
        try {
            const response = await fetch(`${this._baseUrl}/validate-guide-nic-dob`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ nic, dob }),
            });

            const jsonResponse = await response.json();

            if (response.ok) {
                return jsonResponse.isValid === true;
            } else {
                const message = jsonResponse.message || 'Guide NIC/DOB validation failed';
                throw new Error(message);
            }
        } catch (e) {
            throw new Error(`Failed to validate Guide NIC and DOB: ${e.message || e}`);
        }
    }

    /**
     * Validates Guide NIC and Gender against the API.
     * @param {string} nic
     * @param {number} genderId
     * @returns {Promise<boolean>} True if valid.
     * @throws {Error} If validation fails or network error occurs.
     */
    async validateGuideNicGender(nic, genderId) {
        try {
            const response = await fetch(`${this._baseUrl}/validate-guide-nic-gender`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ nic, genderId }),
            });

            const jsonResponse = await response.json();

            if (response.ok) {
                return jsonResponse.isValid === true;
            } else {
                const message = jsonResponse.message || 'Guide NIC/Gender validation failed';
                throw new Error(message);
            }
        } catch (e) {
            throw new Error(`Failed to validate Guide NIC and Gender: ${e.message || e}`);
        }
    }

    /**
     * Validates City and Zip/Postal code against the API.
     * @param {string} city
     * @param {string} zip
     * @returns {Promise<boolean>} True if valid.
     * @throws {Error} If validation fails or network error occurs.
     */
    async validateCityZip(city, zip) {
        try {
            const response = await fetch(`${this._baseUrl}/validate-city-zip`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ city, zip }),
            });

            const jsonResponse = await response.json();

            if (response.ok) {
                return jsonResponse.isValid === true;
            } else {
                const message = jsonResponse.message || 'City/Zip validation failed';
                throw new Error(message);
            }
        } catch (e) {
            throw new Error(`Failed to validate city and zip: ${e.message || e}`);
        }
    }

    /**
     * Validates City and District ID against the API.
     * @param {string} city
     * @param {number} districtId
     * @returns {Promise<boolean>} True if valid.
     * @throws {Error} If validation fails or network error occurs.
     */
    async validateCityDistrict(city, districtId) {
        try {
            const response = await fetch(`${this._baseUrl}/validate-city-district`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ city, districtId }),
            });

            const jsonResponse = await response.json();

            if (response.ok) {
                return jsonResponse.isValid === true;
            } else {
                const message = jsonResponse.message || 'City/District validation failed';
                throw new Error(message);
            }
        } catch (e) {
            throw new Error(`Failed to validate City and District: ${e.message || e}`);
        }
    }
}